import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useApolloClient, useQuery } from '@apollo/client';
import Path from 'path-to-regexp';
import { useHistory, useLocation } from 'react-router-dom';

import { Button } from '@/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shadcn/dialog';

import { gql } from '@/apollo/gql';
import type {
  Action,
  Group,
  TraceByIdsQuery,
  WorkflowQueryQuery
} from '@/apollo/graphql';
import { env } from '@/constant/env';
import {
  ROUTE_NEW_LINK,
  ROUTE_WORKFLOW_BATCH_ACTIONS
} from '@/constant/routes';
import {
  AVAILABLE_ACTIONS_FOR_FILTER_QUERY,
  COUNT_MATCHING_FOR_ACTION_QUERY
} from '@/gql/queries/batch-action.gql';
import { FAST_WORKFLOW_COUNT_QUERY } from '@/gql/queries/fast-workflow-overview.gql';
import { useMyRunningBatchJob } from '@/hooks/useMyRunningBatchJob';
import type { NextActions } from '@/types/nextActions.types';
import { getNextActionsArray, toTransitionsToNextActions } from '@/utils';
import { createBatchFormTarget } from '../batchAction/batchHandoff';
import type { BatchSelection } from '../batchAction/batchHandoff.schema';
import {
  readPersistedToast,
  showBatchProgressToast
} from '../batchAction/batchProgressToast';
import { ActionsList } from '../ui/actionsList/actionList';
import type { NextAction } from '../ui/actionsList/actionList.types';
import type { Link } from '../ui/actionsList/groupActionsList';
import Vaul from '../ui/vaul/vaul';
import { useWorkflowOverviewContext } from './context.workflowOverview';
import { getFiltersFromUserDisplayConfig } from './utils';

const GET_NEXT_ACTIONS = gql(`
  query traceByIds($traceIds: [UUID!]!) {
    traceByIds(ids: $traceIds) {
      nodes {
        rowId
        state {
          nextActions
        }
      }
    }
  }
`);

type WorkflowOverviewActionsProps = {
  workflow: NonNullable<WorkflowQueryQuery['workflowByRowId']>;
};

type Actions = Record<string, Action>;

type BatchFormResult = {
  batchJobId?: string | null;
};

/**
 * Confirmation-dialog copy for filter-scan batch submit. Extracted to keep the
 * JSX free of nested ternaries: shows the exact vs. matching count and, when
 * the exact set is smaller, how many traces get skipped. The exact count (the
 * number of traces the action will actually run on) is emphasised in bold.
 */
const batchConfirmDescription = (
  confirm: { matching: number; totalMatching: number; loading: boolean } | null
): React.ReactNode => {
  if (!confirm) {
    return '';
  }
  if (confirm.loading) {
    return 'Computing exact impact…';
  }
  const { matching, totalMatching } = confirm;
  const plural = totalMatching === 1 ? '' : 's';
  const skipped =
    matching < totalMatching
      ? ` — ${totalMatching - matching} do not have this action available and will be skipped.`
      : '.';
  return (
    <>
      This will run on <strong>{matching}</strong>{' '}
      <strong>of {totalMatching}</strong> matching trace{plural}
      {skipped}
    </>
  );
};

export const WorkflowOverviewActions = React.memo(
  (props: WorkflowOverviewActionsProps) => {
    const [firstActions, setFirstActions] = useState<NextAction[]>([]);
    const [possibleActions, setPossibleActions] = useState<NextAction[]>([]);
    const { workflow } = props;

    const {
      selectedTraceIds,
      showNewTracesTray,
      showTracesUpdateTray,
      toggleNewTracesTray,
      toggleUpdateTracesTray,
      userDisplayConfig,
      archiveDisplayTraces
    } = useWorkflowOverviewContext();

    const { groups, rowId: workflowRowId, config } = workflow;
    const { actions, initActions, transitions } = config || {};

    const history = useHistory();
    const location = useLocation<BatchFormResult>();

    // --- Scalable Batch Actions (STMN-3358..3361), gated behind USE_BATCH_ACTION.
    const batchEnabled = env.FEATURE_FLAGS.USE_BATCH_ACTION;

    // The active overview filter, reused as the batch selection filter (same
    // FastTraceStateFilter shape the materialize activity consumes).
    const activeFilter = useMemo(
      () => getFiltersFromUserDisplayConfig(userDisplayConfig),
      [userDisplayConfig]
    );
    const hasActiveFilter = batchEnabled && !!activeFilter;

    // The filter a batch actually runs on. `activeFilter` (data/meta) does NOT
    // carry the archive-display state, so on its own a filter-mode batch would
    // count + materialize ARCHIVED traces the overview is hiding (the "173 of
    // 172" mismatch). Merge the archive toggle into the filter — mirroring the
    // backend withArchivedFilter used by the overview count — so the batch
    // operates on exactly the set the user sees: toggle OFF -> archived:false
    // (exclude), toggle ON -> archived:true (the archived set they're viewing).
    const batchFilter = useMemo(() => {
      const archivedFilter = { archived: archiveDisplayTraces };
      if (!activeFilter) {
        return archivedFilter;
      }
      return { and: [archivedFilter, activeFilter] };
    }, [activeFilter, archiveDisplayTraces]);

    const reattachedRef = useRef(false);
    const { runningJob: reattachRunningJob } = useMyRunningBatchJob(
      String(workflowRowId),
      { skip: !batchEnabled }
    );

    const startBatchTray = useCallback(
      (jobId: string | null) => {
        if (!jobId) {
          return;
        }
        showBatchProgressToast(
          {
            jobId,
            workflowId: String(workflowRowId)
          },
          () => {
            const path = Path.compile(ROUTE_WORKFLOW_BATCH_ACTIONS)({
              id: workflowRowId
            });
            history.push(`${path}?job=${jobId}`);
          }
        );
      },
      [history, workflowRowId]
    );

    // Count of traces matching the active filter (for the "apply to all" banner).
    const { data: countData } = useQuery(FAST_WORKFLOW_COUNT_QUERY, {
      variables: {
        workflowId: workflowRowId,
        filter: activeFilter,
        archived: archiveDisplayTraces
      },
      skip: !hasActiveFilter,
      fetchPolicy: 'cache-and-network'
    });
    const matchingCount = countData?.workflowCount?.fastTraceStatesCount ?? 0;

    // Navigate to the newLink form so the user fills in the action's formData
    // (STMN-3359). The form submits the batch itself (server signs each link)
    // and navigates back with the created `batchJobId`, which the effect below
    // uses to open the progress tray. `groupKey` (from the selected link) IS the
    // batch groupLabel; the form reads it from the query string to build the action.
    //
    // The selection travels in the router state plus a sessionStorage stash
    // (createBatchFormTarget), never in the query string: ~215 uuids already
    // overflow the 8 KB ingress URL limit and the page 414s on any real HTTP hit.
    const navigateToBatchForm = useCallback(
      (link: Link, selection: BatchSelection, traceCount: number) => {
        const baseUrl = Path.compile(ROUTE_NEW_LINK)({
          wfid: String(workflowRowId)
        });
        const { path } = createBatchFormTarget({
          baseUrl,
          groupKey: link.groupKey,
          actionKey: link.actionKey,
          selection,
          traceCount
        });
        history.push(path, {
          from: history.location,
          batch: selection
        });
      },
      [workflowRowId, history]
    );

    // "Filter-scan mode" — tray opened with 0 selected + an active filter.
    // We ask the server (via AVAILABLE_ACTIONS_FOR_FILTER_QUERY) which actions
    // any filter-matching trace can run, then use the returned pairs as the
    // tray's action list. Picking one runs a filter-mode batch after a
    // pre-submit confirmation modal seeded by countMatchingForAction.
    const isFilterScanMode =
      showTracesUpdateTray && !selectedTraceIds.length && hasActiveFilter;

    const applyToAllMatching = useCallback(
      (link: Link, exactCount?: number) => {
        navigateToBatchForm(
          link,
          {
            mode: 'filter',
            workflowId: String(workflowRowId),
            // batchFilter (not activeFilter) so materialize honors the archive
            // toggle and never pulls hidden archived traces into the job.
            filter: batchFilter
          },
          exactCount ?? matchingCount
        );
      },
      [navigateToBatchForm, workflowRowId, batchFilter, matchingCount]
    );

    // Pre-submit confirmation for filter-mode batches. Filter-scan promised
    // "these actions can be batched" (existence check); before the actual
    // job launches, we run an exact count so the user sees the true impact
    // — a broad filter can drop from `matchingCount` to a much smaller number
    // once next_actions narrowing is applied on the picked action. The count
    // query is fired lazily on Perform-action click; the modal blocks until
    // the user explicitly confirms.
    const apolloClient = useApolloClient();
    const [pendingConfirm, setPendingConfirm] = useState<{
      link: Link;
      matching: number;
      totalMatching: number;
      loading: boolean;
    } | null>(null);
    const openConfirmation = useCallback(
      async (link: Link) => {
        setPendingConfirm({
          link,
          matching: 0,
          totalMatching: matchingCount,
          loading: true
        });
        try {
          const { data: countData } = await apolloClient.query({
            query: COUNT_MATCHING_FOR_ACTION_QUERY,
            variables: {
              input: {
                workflowId: String(workflowRowId),
                groupLabel: link.groupKey,
                actionKey: link.actionKey,
                // batchFilter so the exact "N of M" count excludes archived to
                // match the banner + what materialize will actually process.
                filter: batchFilter
              }
            },
            fetchPolicy: 'network-only'
          });
          const exact = countData?.countMatchingForAction?.count ?? 0;
          setPendingConfirm(prev =>
            prev ? { ...prev, matching: exact, loading: false } : prev
          );
        } catch {
          // Surface the failure to the user by closing the modal — the
          // ActionsList remains open so they can retry. A silent "0 matching"
          // fallback would be worse than an honest re-click here.
          setPendingConfirm(null);
        }
      },
      [apolloClient, workflowRowId, batchFilter, matchingCount]
    );

    const performBatch = useCallback(
      (link: Link, traceIds: string[]) => {
        // Filter-scan mode: 0 rows checked + a filter is active, so the tray
        // was populated from server-side availableActionsForFilter. Do NOT
        // submit yet — first surface the exact impact count in a confirmation
        // modal (see openConfirmation). The batch launches on user confirm.
        if (!traceIds.length && isFilterScanMode) {
          openConfirmation(link);
          return;
        }
        navigateToBatchForm(
          link,
          { mode: 'explicit', ids: traceIds },
          traceIds.length
        );
      },
      [navigateToBatchForm, isFilterScanMode, openConfirmation]
    );

    const openedJobIdRef = useRef<string | null>(null);
    useEffect(() => {
      const jobId = location.state?.batchJobId;
      if (!jobId) {
        return;
      }
      if (openedJobIdRef.current === jobId) {
        return;
      }
      openedJobIdRef.current = jobId;

      startBatchTray(jobId);

      history.replace(location.pathname + location.search);
    }, [location, history, startBatchTray]);

    // If a batch is still running server-side after a reload, re-surface the
    // live progress toast for it (same surface as a fresh submit). Skipped when
    // the router-state handoff just opened one (openedJobIdRef), or once we've
    // already re-attached (reattachedRef) so a later poll/render can't re-pop
    // the toast.
    useEffect(() => {
      if (reattachedRef.current) {
        return;
      }
      if (openedJobIdRef.current) {
        return;
      }
      if (!reattachRunningJob?.id) {
        return;
      }
      reattachedRef.current = true;
      const jobId = reattachRunningJob.id;
      showBatchProgressToast(
        { jobId, workflowId: String(workflowRowId) },
        () => {
          const path = Path.compile(ROUTE_WORKFLOW_BATCH_ACTIONS)({
            id: workflowRowId
          });
          history.push(`${path}?job=${jobId}`);
        }
      );
    }, [reattachRunningJob, history, workflowRowId]);

    const restoredToastRef = useRef(false);
    useEffect(() => {
      if (restoredToastRef.current || !batchEnabled) {
        return;
      }
      restoredToastRef.current = true;
      const persisted = readPersistedToast();
      if (!persisted || persisted.workflowId !== String(workflowRowId)) {
        return;
      }
      showBatchProgressToast(
        {
          jobId: persisted.jobId,
          workflowId: persisted.workflowId
        },
        () => {
          const path = Path.compile(ROUTE_WORKFLOW_BATCH_ACTIONS)({
            id: workflowRowId
          });
          history.push(`${path}?job=${persisted.jobId}`);
        }
      );
    }, [batchEnabled, history, workflowRowId]);

    // Feed the possible-actions query with the explicit selection. In
    // filter-scan mode we skip this query entirely and use the server-driven
    // availableActionsForFilter list below — sampling the visible page cannot
    // scale to 100k+ filter sets, and the server's next_actions aggregation
    // matches materialize byte-for-byte (same NEXT_ACTIONS_PREDICATE).
    const { data, loading: loadingExplicit } = useQuery(GET_NEXT_ACTIONS, {
      variables: {
        traceIds: selectedTraceIds
      },
      skip: !selectedTraceIds.length,
      nextFetchPolicy: 'cache-and-network'
    });

    // Filter-scan mode: ask the server "which (group, action) pairs can be
    // batched over this filter set?" instead of sampling client-side. Cheap
    // existence check (SELECT DISTINCT over unnested next_actions on the
    // filtered set), GIN-indexed, no sampling error. Skipped whenever the
    // user has manually selected rows — that path uses the explicit
    // intersection above.
    const { data: availableData, loading: loadingAvailable } = useQuery(
      AVAILABLE_ACTIONS_FOR_FILTER_QUERY,
      {
        variables: {
          input: {
            workflowId: String(workflowRowId),
            // batchFilter so the pickable actions reflect the archive toggle —
            // an action only available on a hidden archived trace must not show.
            filter: batchFilter
          }
        },
        skip: !isFilterScanMode,
        fetchPolicy: 'cache-and-network'
      }
    );

    const loading = loadingExplicit || loadingAvailable;

    // Feed init actions on tray status change
    const computeFirstActions = async () => {
      const nextActions = transitions?.['-']
        ? await toTransitionsToNextActions(transitions['-'])
        : initActions;

      setFirstActions(
        getNextActionsArray({
          nextActions: nextActions as NextActions,
          groups: groups.nodes as Group[],
          actions: Object.values(actions as Actions)
        })
      );
    };

    useEffect(() => {
      if (showNewTracesTray && actions) {
        computeFirstActions();
      } else {
        setFirstActions([]);
      }
    }, [showNewTracesTray]);

    // Feed possible actions on tray status change
    const computePossibleActions = (
      selectedTraceStates: NonNullable<TraceByIdsQuery['traceByIds']>['nodes']
    ) => {
      const allNextActions = selectedTraceStates.map(
        state => state?.state?.nextActions as NextActions
      );

      // Get the intersection of all nextActions for all selected traces
      // and normalize them to the "v2" next actions structure
      // ({ [group]: { action1: bool, action2: bool, ... } })
      const nextActionsIntersection = allNextActions.reduce(
        (intersection, nextActions) => {
          // initialize possible actions with nextActions of the first trace
          if (Object.keys(intersection).length === 0) {
            const result = {};
            Object.keys(nextActions).forEach(groupLabel => {
              if (!nextActions[groupLabel]) return;
              result[groupLabel] = {};
              if (Array.isArray(nextActions[groupLabel])) {
                // v1
                if (!nextActions[groupLabel].length) return;
                nextActions[groupLabel].forEach(actionKey => {
                  result[groupLabel][actionKey] = true;
                });
              } else {
                // v2
                result[groupLabel] = nextActions[groupLabel];
              }
            });
            return result;
          }

          // Abort if no next actions possible
          if (!Object.keys(intersection).length) return intersection;

          // otherwise compute intersection of current possible actions and next actions of the current trace
          Object.keys(nextActions).forEach(groupLabel => {
            if (
              !(intersection[groupLabel] && intersection['*']) ||
              groupLabel !== '*'
            ) {
              return;
            }
            const newActions = {};
            if (Array.isArray(nextActions[groupLabel])) {
              // v1
              nextActions[groupLabel].forEach(actionKey => {
                if (!intersection[groupLabel][actionKey]) return;
                newActions[actionKey] = true;
              });
            } else {
              // v2
              Object.entries(nextActions[groupLabel]).forEach(
                ([actionKey, condition]) => {
                  if (
                    // Named intersection
                    intersection[groupLabel][actionKey] ||
                    // Wildcards
                    intersection[groupLabel]['*'] ||
                    intersection['*'][actionKey] ||
                    intersection['*']['*']
                  ) {
                    newActions[actionKey] = condition;
                  }
                }
              );
            }
            if (Object.keys(newActions).length) {
              intersection[groupLabel] = newActions;
            } else {
              delete intersection[groupLabel];
            }
          });

          // If the group is not featured in next actions, drop all possibilities from that group
          Object.keys(intersection).forEach(groupLabel => {
            if (!(nextActions[groupLabel] || nextActions['*'])) {
              delete intersection[groupLabel];
            }
          });

          return intersection;
        },
        {} as NextActions
      );

      // If multiple traces are selected, do not include actions that use the data importer or editor,
      // as these are tailored towards single traces.
      const filteredActions =
        selectedTraceStates.length === 1
          ? Object.values(actions as Actions)
          : Object.values(actions as Actions).filter(
              a => !(a.dataImporter || a.dataEditor)
            );

      return setPossibleActions(
        getNextActionsArray({
          nextActions: nextActionsIntersection as NextActions,
          groups: groups.nodes as Group[],
          actions: filteredActions as Action[]
        })
      );
    };

    useEffect(() => {
      if (!showTracesUpdateTray) {
        setPossibleActions([]);
        return;
      }
      // Explicit mode: intersect nextActions across the manually-selected rows
      // (the historical path).
      if (selectedTraceIds.length > 0) {
        if (data?.traceByIds?.nodes) {
          computePossibleActions(data.traceByIds.nodes);
        } else {
          setPossibleActions([]);
        }
        return;
      }
      // Filter-scan mode: turn the server's flat (groupLabel, actionKey) list
      // into a v2 next-actions shape ({group: {action: true}}) and delegate
      // to getNextActionsArray so the tray renders identical rows in both
      // paths (groups, action metadata, priority settings all reuse the
      // same UI code path).
      if (isFilterScanMode && availableData?.availableActionsForFilter?.pairs) {
        const pairs = availableData.availableActionsForFilter.pairs;
        const synthetic: NextActions = {};
        for (const p of pairs) {
          if (!synthetic[p.groupLabel]) {
            synthetic[p.groupLabel] = {};
          }
          (synthetic[p.groupLabel] as Record<string, boolean>)[p.actionKey] =
            true;
        }
        // Match the explicit-multi-select behavior: exclude data-importer /
        // -editor actions since they are tailored to single traces.
        const filteredActions = Object.values(actions as Actions).filter(
          a => !(a.dataImporter || a.dataEditor)
        );
        setPossibleActions(
          getNextActionsArray({
            nextActions: synthetic,
            groups: groups.nodes as Group[],
            actions: filteredActions as Action[]
          })
        );
        return;
      }
      setPossibleActions([]);
    }, [
      showTracesUpdateTray,
      selectedTraceIds,
      isFilterScanMode,
      data,
      availableData,
      actions,
      groups.nodes
    ]);

    const tracePluraliser = selectedTraceIds.length > 1 ? 's' : '';

    const newTracesTrayMessage = useMemo(() => {
      if (showNewTracesTray && !firstActions.length) {
        return 'You have no permission to create a trace on this workflow.';
      }
      return 'The following trace initialisations are available.';
    }, [showNewTracesTray, firstActions]);

    const updateTracesTrayMessage = useMemo(() => {
      if (showTracesUpdateTray && selectedTraceIds.length) {
        if (!(possibleActions.length || loading)) {
          return `There is no action available for the selected trace${tracePluraliser}.`;
        }
        return `The following actions are available for the selected trace${tracePluraliser}.`;
      }
      if (isFilterScanMode) {
        if (!(possibleActions.length || loading)) {
          return `There is no action common to the ${matchingCount} matching trace${matchingCount === 1 ? '' : 's'}.`;
        }
        return `Picking an action will run it on all ${matchingCount} trace${matchingCount === 1 ? '' : 's'} matching the current filter.`;
      }
      return 'Please select at least one trace.';
    }, [
      showTracesUpdateTray,
      selectedTraceIds,
      possibleActions,
      isFilterScanMode,
      matchingCount,
      loading
    ]);

    return (
      <>
        <Vaul
          description={newTracesTrayMessage}
          open={showNewTracesTray}
          setOpen={toggleNewTracesTray}
          title="Create New Trace"
        >
          <ActionsList
            nextActions={firstActions}
            toggleTray={toggleNewTracesTray}
            workflowId={workflowRowId}
          />
        </Vaul>
        <Vaul
          description={updateTracesTrayMessage}
          open={showTracesUpdateTray}
          setOpen={toggleUpdateTracesTray}
          title={
            isFilterScanMode
              ? `Update ${matchingCount} matching trace${matchingCount === 1 ? '' : 's'}`
              : `Update the selected trace${tracePluraliser}`
          }
        >
          <ActionsList
            batch={
              batchEnabled
                ? {
                    onPerformBatch: performBatch,
                    hasActiveFilter,
                    matchingCount,
                    onApplyToAllMatching: applyToAllMatching,
                    isFilterScanMode
                  }
                : undefined
            }
            canSelect
            loading={loading}
            nextActions={possibleActions}
            toggleTray={toggleUpdateTracesTray}
            traceIds={selectedTraceIds}
            workflowId={workflowRowId}
          />
        </Vaul>
        {/*
          Filter-mode pre-submit confirmation. Opens when the user picks an
          action in filter-scan mode, blocks until they explicitly confirm.
          Shows the exact count (server-computed via countMatchingForAction)
          alongside the filter-matching total so the user can see how many
          traces will actually receive the action vs. how many merely matched
          the filter — no silent narrowing.
        */}
        <Dialog
          onOpenChange={open => {
            if (!open) setPendingConfirm(null);
          }}
          open={pendingConfirm !== null}
        >
          {/* Above the Vaul tray (z-50) this modal is opened from. */}
          <DialogContent overlayClassName="z-[60]">
            <DialogHeader>
              <DialogTitle>Run batch action?</DialogTitle>
              <DialogDescription>
                {batchConfirmDescription(pendingConfirm)}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={() => setPendingConfirm(null)} variant="outline">
                Cancel
              </Button>
              <Button
                disabled={!pendingConfirm || pendingConfirm.loading}
                onClick={() => {
                  if (!pendingConfirm) return;
                  const { link, matching } = pendingConfirm;
                  setPendingConfirm(null);
                  applyToAllMatching(link, matching);
                }}
              >
                {pendingConfirm && !pendingConfirm.loading
                  ? `Run on ${pendingConfirm.matching}`
                  : 'Run'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }
);
